import {
  Question, Section,
} from '@gamiphy/bl/survey';
import { AppSurveyDto, SurveyQuestionDto } from '@coretava/api/events-service';
import { useUserSurveysApi } from '@gamiphy/loyalty-station-desktop/hooks/apis';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { CampaignUserStore } from '@gamiphy/loyalty-station-desktop/store/campaignUsers';
import { SurveyStore } from '@gamiphy/loyalty-station-desktop/store/survey';
import { delay } from '@gamiphy/utils/general';
import React from 'react';
import { Control, FormState, useForm } from 'react-hook-form';

type SurveyDataType<T> = T extends 'section' ? Section<Question> : Question;
interface SurveyFormReturnType<T> {
  control: Control<Record<string, string | string[]>, object>;
  formState: FormState<Record<string, string | string[]>>
  data: SurveyDataType<T>;
  onSubmit: (cb: () => void) => (e?: React.FormEvent<object>) => void;
  handleBack: () => void;
  percent: number;
  isFirst: boolean;
  shouldSubmit: boolean;
  dataIndex: number;
  questions: SurveyQuestionDto[];
  isSubmitting: boolean
}

const useSurveyForm = <T extends 'section' | 'question'>({
  groupBy,
  appSurvey,
}: { groupBy: T, appSurvey: AppSurveyDto }): SurveyFormReturnType<T> => {
  const { control, handleSubmit, formState } = useForm<
    Record<string, string | string[]>
  >({
    mode: 'onChange',
  });
  const [dataIndex, setDataIndex] = React.useState(0);
  const isFirst = React.useMemo(() => dataIndex === 0, [dataIndex]);
  const sections = React.useMemo(() => appSurvey.sections ?? [], [appSurvey]);
  const questions = React.useMemo(() => sections.flatMap((section) => section.questions), [sections]);
  const isCampaignSurvey = React.useMemo(
    () => CampaignUserStore.surveyCampaignUser?.campaign.booster?.id === appSurvey.id,
    [appSurvey, CampaignUserStore.surveyCampaignUser],
  );

  const userSurveysApi = useUserSurveysApi();

  const surveyData = React.useMemo(() => {
    if (groupBy === 'section') {
      return sections[dataIndex];
    }
    return questions[dataIndex];
  }, [sections, questions, dataIndex, groupBy]);

  const shouldSubmit = React.useMemo(() => {
    if (groupBy === 'section') {
      return dataIndex === sections.length - 1;
    }
    return dataIndex === questions.length - 1;
  }, [dataIndex, groupBy, sections, questions]);

  const onSubmit = React.useCallback((cb: () => void) => handleSubmit(async (data) => {
    if (shouldSubmit) {
      if (isCampaignSurvey) {
        await userSurveysApi.userSurveysControllerSubmitByUserToken(AppStore.app.id, appSurvey.id, {
          answers: Object.entries(data).map(([key, value]) => ({
            answer: value,
            answered: true,
            question: key,
          })),
        });
        await delay(1000);
        void CampaignUserStore.fetchCampaignUsers();
      } else {
        await Promise.all(Object.entries(data)
          .map(async ([key, value]) => {
            await SurveyStore.answerQuestion(key, value);
          }));
        await SurveyStore.submitSurvey();
      }
      cb();
    } else {
      setDataIndex((i) => i + 1);
    }
  }), [handleSubmit, shouldSubmit, isCampaignSurvey, userSurveysApi]);

  const handleBack = React.useCallback(() => {
    setDataIndex((i) => i - 1);
  }, []);

  const percentageAnswered = React.useMemo(
    () => ((dataIndex + 1) / questions.length) * 100,
    [dataIndex, questions],
  );
  return {
    control,
    formState,
    data: surveyData as SurveyDataType<T>,
    onSubmit,
    handleBack,
    percent: percentageAnswered,
    isFirst,
    shouldSubmit,
    dataIndex,
    questions,
    isSubmitting: formState.isSubmitting,
  };
};

export { useSurveyForm };
